/* eslint-disable @typescript-eslint/no-explicit-any */

import { ProfileFields } from 'interfaces/UserProfile';

export interface ZephrRegistrationData {
  identifiers?: {
    email_address: string;
  };
  attributes?: {
    'first-name': string;
    'last-name': string;
  };
  validators: {
    password?: string;
    token_exchange?: string;
  };
}

interface ZephrEmailVerification {
  email: string;
  redirectUrl: string;
}

interface RegistrationResponse {
  success: boolean;
  status: number;
  error?: string;
  message?: string;
}

interface PhoneVerifyResponse {
  status: number;
  data: {
    error: string;
  };
}

export interface UserProfileResponse {
  success: boolean;
  data?: {
    'first-name': string;
    'last-name': string;
    'phone-number': string;
  };
  error?: string;
  message?: string;
}

export interface UserProfileUpdateResponse {
  success: boolean;
  error?: string;
  message?: string;
}

export default class ZephrService {
  // was "window.ZephrConfiguration?.apiBaseUrl" but removed so we use Zephr directly as much as possible
  // to avoid any cache and other issues using our proxy backend
  // for local testing you can hardcode something like https://prod-origin.finansavisen.no
  static baseUrl = '';

  public static async register(regData: ZephrRegistrationData): Promise<RegistrationResponse> {
    try {
      const response = await fetch(this.baseUrl ? `${this.baseUrl}/api/register` : '/blaize/register', {
        method: 'POST',
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(regData),
      });

      return {
        success: response.status === 200,
        status: response.status,
      };
    } catch (error: unknown) {
      return {
        success: false,
        status: 500,
        error: error as string,
        message: 'Noe gikk galt, prøv igjen eller kontakt oss',
      };
    }
  }

  public static async sendVerificationEmail(data: ZephrEmailVerification): Promise<RegistrationResponse> {
    try {
      // for our backend we only need form data, and directly to Zephr we need a complete payload (used on Bors)
      const bodyData = this.baseUrl
        ? data
        : {
            identifiers: {
              email_address: data.email,
            },
            delivery: {
              method: 'email',
              destination: data.email,
              action: 'register',
              redirect: data.redirectUrl,
            },
          };

      const response = await fetch(this.baseUrl ? `${this.baseUrl}/api/verify-email` : '/blaize/token-exchange', {
        method: 'POST',
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(bodyData),
      });

      return {
        success: response.status === 201,
        status: response.status,
        ...(await response.json()),
      };
    } catch (error) {
      return {
        success: false,
        status: 500,
        error: error as string,
        message: 'Oops! Something went wrong :-(',
      };
    }
  }

  public static async updateUserProfile(attributes: any): Promise<UserProfileUpdateResponse> {
    try {
      const response = await fetch(this.baseUrl ? `${this.baseUrl}/api/update-profile` : '/blaize/profile', {
        method: 'POST',
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(attributes),
      });

      return {
        success: response.status === 200,
      };
    } catch (error: any) {
      return {
        success: false,
        error: error.message,
        message: 'Oops! Something went wrong :-(',
      };
    }
  }

  public static async getUserProfile(): Promise<UserProfileResponse> {
    try {
      const response = await fetch(this.baseUrl ? `${this.baseUrl}/api/profile-info` : '/blaize/profile', {
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json',
        },
      });

      return {
        success: response.status === 200,
        data: await response.json(),
      };
    } catch (error) {
      // eslint-disable-next-line no-console
      console.log('Get profile error?', error);

      return {
        success: false,
        message: 'Oops! Something went wrong :-(',
      };
    }
  }

  public static async sendSmsVerificationCode(data: ProfileFields): Promise<PhoneVerifyResponse> {
    try {
      const phoneVerifyResponse = await fetch(
        `${window.ZephrConfiguration?.apiBaseUrl || 'https://www.finansavisen.no'}/api/verify-phone`,
        {
          method: 'POST',
          body: JSON.stringify(data),
          headers: {
            Accept: 'application/json',
            'Content-Type': 'application/json',
          },
        }
      );

      return {
        status: phoneVerifyResponse.status,
        data: await phoneVerifyResponse.json(),
      };
    } catch (e) {
      return {
        status: 500,
        data: {
          error: 'Noe gikk galt, prøv igjen eller kontakt oss.',
        },
      };
    }
  }
}
